import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchProducts } from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO_IMG =
    "https://customer-assets-lqy194kg.emergentagent.net/job_crown-oak-keyfobs/artifacts/7nxzq19h_DSC01529.webp";
const CRAFT_IMG =
    "https://customer-assets-lqy194kg.emergentagent.net/job_crown-oak-keyfobs/artifacts/ik0kpqng_DSC01537.webp";

const Home = () => {
    const [products, setProducts] = useState([]);

    useEffect(() => {
        fetchProducts().then(setProducts).catch(() => {});
    }, []);

    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section
                data-testid="home-hero"
                className="relative min-h-[86vh] flex items-center overflow-hidden"
            >
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMG}
                        alt="Sailboat on calm water"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[#0A2342]/40" />
                </div>
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 text-[#FBF9F6] w-full">
                    <div className="max-w-2xl">
                        <p className="fade-up text-[11px] tracking-[0.35em] uppercase text-[#FBF9F6]/80 mb-6">
                            Est. 2026 · Crown &amp; Oak
                        </p>
                        <h1 className="fade-up delay-1 font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                            Heritage,
                            <br />
                            hand-stitched.
                        </h1>
                        <p className="fade-up delay-2 mt-6 max-w-lg text-base md:text-lg text-[#FBF9F6]/85 leading-relaxed">
                            Small-batch needlepoint key fobs made with cream
                            canvas, cognac leather, and polished silver — an
                            everyday heirloom for the country club and beyond.
                        </p>
                        <div className="fade-up delay-3 mt-10 flex flex-wrap gap-4">
                            <Link
                                data-testid="hero-shop-btn"
                                to="/shop"
                                className="btn-primary bg-[#FBF9F6] text-[#0A2342] hover:!bg-[#FBF9F6]/90"
                                style={{ background: "#FBF9F6", color: "#0A2342" }}
                            >
                                Shop the Collection
                                <ArrowRight size={16} />
                            </Link>
                            <Link
                                data-testid="hero-about-btn"
                                to="/about"
                                className="btn-outline border-[#FBF9F6] text-[#FBF9F6] hover:!bg-[#FBF9F6] hover:!text-[#0A2342]"
                                style={{ borderColor: "#FBF9F6", color: "#FBF9F6" }}
                            >
                                Our Story
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURED */}
            <section
                data-testid="home-featured"
                className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32"
            >
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                    <div>
                        <p className="text-[11px] tracking-[0.35em] uppercase text-[#9E5A35] mb-4">
                            The Collection
                        </p>
                        <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#0A2342] leading-[1.05]">
                            Featured fobs.
                        </h2>
                    </div>
                    <Link
                        data-testid="featured-view-all"
                        to="/shop"
                        className="text-[11px] tracking-[0.22em] uppercase text-[#0A2342] hover:text-[#9E5A35] flex items-center gap-2"
                    >
                        View all <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                    {products.slice(0, 4).map((p) => (
                        <ProductCard key={p.id} product={p} />
                    ))}
                </div>
            </section>

            {/* CRAFT */}
            <section
                data-testid="home-craftsmanship"
                className="bg-white border-y border-[#E5E0D8]"
            >
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
                    <div className="order-2 md:order-1">
                        <p className="text-[11px] tracking-[0.35em] uppercase text-[#9E5A35] mb-4">
                            Craftsmanship
                        </p>
                        <h2 className="font-serif-display text-4xl md:text-5xl text-[#0A2342] leading-[1.05]">
                            Every stitch, by hand.
                        </h2>
                        <p className="mt-6 text-base md:text-lg text-[#1A1A1A]/80 leading-relaxed max-w-lg">
                            Each fob begins as blank cream canvas. Our
                            needlepointers stitch each design one thread at a
                            time — then finish it with full-grain cognac leather
                            and a polished silver clasp built to last a
                            lifetime.
                        </p>
                        <ul className="mt-8 space-y-3 text-sm text-[#0A2342]">
                            {[
                                "Hand-stitched needlepoint",
                                "Full-grain cognac leather trim",
                                "Polished silver-tone hardware",
                                "Small-batch, made to last",
                            ].map((l) => (
                                <li
                                    key={l}
                                    className="flex items-center gap-3"
                                >
                                    <span className="w-8 h-px bg-[#9E5A35]" />
                                    {l}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="order-1 md:order-2 relative">
                        <div className="aspect-[4/5] overflow-hidden rounded-sm">
                            <img
                                src={CRAFT_IMG}
                                alt="Leather crafting tools"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="hidden md:block absolute -bottom-6 -left-6 bg-[#0A2342] text-[#FBF9F6] px-8 py-6 max-w-[220px]">
                            <div className="font-serif-display text-3xl leading-none">
                                C&amp;O
                            </div>
                            <div className="text-[10px] tracking-[0.22em] uppercase mt-2 text-[#FBF9F6]/70">
                                Est. 2026
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32 text-center">
                <p className="text-[11px] tracking-[0.35em] uppercase text-[#9E5A35] mb-4">
                    Everyday heirlooms
                </p>
                <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#0A2342] max-w-3xl mx-auto leading-[1.05]">
                    A small piece of the club, wherever your keys go.
                </h2>
                <div className="mt-10">
                    <Link
                        data-testid="home-cta-shop"
                        to="/shop"
                        className="btn-primary"
                    >
                        Shop Now <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default Home;
